import React from 'react';

// Libraries
import { SortingRule } from 'react-table';

// Components
import LegacyModelsPortfoliosList from '@/views/components/modals/modal-content/LegacyModelsPortfoliosList';
import { GenericEmptyState } from '@/views/components/misc';
import { LoadingSpinner } from '@/views/components/common';
import { PortfoliosTableV2 } from '@/views/components/tables';

// Hooks
import useThemeVersion from '@/hooks/useThemeVersion';

// Enums
import { MODEL_PORTFOLIO_TYPES } from '@/enums/Models';
import { PORTFOLIO_METRICS_FIELDS, PORTFOLIO_TABLE_COLUMNS } from '@/enums/Portfolio';

// Utils
import { formatMessage } from '@/utils/i18n';
import { getPortfolioMissingOrInvalidHoldings } from '@/utils/portfolio/utils';

// Types
import EnterpriseDetails from '@/types/enterprise-detail-info';
import Portfolio from '@/types/portfolio';
import User from '@/types/user';

type Props = {
  query?: string;
  portfolios?: Portfolio[];
  myPortfolios?: Portfolio[];
  enterprisePortfolios?: Portfolio[];
  enterprise?: EnterpriseDetails;
  selectedPortfolios?: Portfolio[];
  loading: boolean;
  hasMorePortfolios: boolean;
  hasMoreMyPortfolios: boolean;
  hasMoreEnterprisePortfolios: boolean;
  loadMorePortfolios: () => void;
  loadMoreMyPortfolios: () => void;
  loadMoreEnterprisePortfolios: () => void;
  resetSelectedPortfolios: () => void;
  onComparePortfolio?: (portfolios: Portfolio[]) => void;
  onSearchPortfolios: (query: string) => void;
  onEditPortfolio?: (portfolio: Portfolio) => void;
  onDeletePortfolio?: (portfolios: Portfolio[]) => void;
  emptyStateAction?: () => void;
  emptyStateHeader: string;
  emptyStateBody?: string;
  emptyStateActionLabel?: string;
  displayEnterpriseLabels?: boolean;
  scrollAncestor?: Window | HTMLElement | string;
  hideStats: boolean;
  comparingPortfolioIds?: string[];
  displayedColumns: PORTFOLIO_TABLE_COLUMNS[];
  displayedMetrics: PORTFOLIO_METRICS_FIELDS[];
  selectedTitle: string;
  user: User;
  selectedOption?: MODEL_PORTFOLIO_TYPES;
  onRowSelectionChange?: (selectedRows: Portfolio[]) => void;
};

type LegacyProps = {
  isThemeLight?: boolean;
  onSort: (sorting: SortingRule[]) => void;
  onSelectPortfolio?: (portfolio: Portfolio) => void;
  onPortfoliosSelected?: (portfolios: Portfolio[]) => void;
  onUnselectPortfolios?: (portfolios: Portfolio[]) => void;
};

type State = {
  initialized: boolean;
};

class ModelsPortfoliosListV2 extends React.Component<Props, State> {
  private scrollContainer?: HTMLElement;

  static defaultProps = {
    includeSearch: true,
    onSearchPortfolios: () => {},
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      initialized: false,
    };
  }

  setContainerRef = (element: HTMLElement) => {
    this.scrollContainer = element;

    if (!this.state.initialized) {
      this.setState({ initialized: true });
    }
  };

  clearSearch = () => {
    this.props.onSearchPortfolios('');
  };

  showLockColumn = () => {
    if (this.props.enterprise) {
    }
  };

  renderNoResults = () => {
    if (this.props.query) {
      return (
        <GenericEmptyState
          header={formatMessage('EmptyState.NoResults.Title')}
          body={formatMessage('EmptyState.NoResults.Text')}
          actionLabel={formatMessage('EmptyState.NoResults.ClearSearch')}
          onAction={() => this.props.onSearchPortfolios('')}
        />
      );
    }

    return <GenericEmptyState header={formatMessage('EmptyState.NoModelPortfolios.Title')} />;
  };

  renderPortfoliosSection = (title: string, loadMore: () => void, portfolios?: Portfolio[], hasMore?: boolean) => {
    const { loading, displayedColumns, displayedMetrics, comparingPortfolioIds, enterprise, selectedOption, onRowSelectionChange } = this.props;

    if (!loading && portfolios?.length === 0) {
      return this.renderNoResults();
    }

    const tableColumns =
      selectedOption === MODEL_PORTFOLIO_TYPES.ALL_MODELS
        ? [...displayedColumns, PORTFOLIO_TABLE_COLUMNS.LOCK, PORTFOLIO_TABLE_COLUMNS.GROUP]
        : [...displayedColumns, PORTFOLIO_TABLE_COLUMNS.LOCK];

    return (
      <PortfoliosTableV2<Portfolio>
        comparingPortfolioIds={comparingPortfolioIds}
        displayedColumns={tableColumns}
        displayedMetrics={displayedMetrics}
        forceSelectable
        hasMore={hasMore}
        locked={!!enterprise}
        onCompare={this.props.onComparePortfolio}
        onDelete={this.props.onDeletePortfolio}
        onEdit={this.props.onEditPortfolio}
        onLoadMore={loadMore}
        onRowSelectionChange={onRowSelectionChange}
        portfolios={portfolios}
        selectedTitle={this.props.selectedTitle}
        canClonePredicate={portfolio => !portfolio.availableActions || portfolio.availableActions.clone}
        canEditPredicate={portfolio => !portfolio.availableActions || portfolio.availableActions.edit}
        missingProductPredicate={portfolio => getPortfolioMissingOrInvalidHoldings(portfolio).length > 0}
      />
    );
  };

  getPortfolios = () => {
    const { portfolios = [], enterprise } = this.props;

    let data = portfolios.slice();

    if (enterprise) {
      data = data.concat(enterprise.portfolios || []);
    }

    return data;
  };

  getScrollableAncestor = () => {
    const { scrollAncestor } = this.props;

    if (scrollAncestor) {
      if (typeof scrollAncestor === 'string') {
        return document.querySelector(scrollAncestor);
      }

      return scrollAncestor;
    }

    return this.scrollContainer;
  };

  render() {
    const {
      portfolios,
      myPortfolios,
      enterprisePortfolios,
      enterprise,
      loading,
      hasMorePortfolios,
      hasMoreMyPortfolios,
      hasMoreEnterprisePortfolios,
      selectedOption,
      loadMorePortfolios,
      loadMoreMyPortfolios,
      loadMoreEnterprisePortfolios,
    } = this.props;

    const portfolioTitles =
      enterprise && formatMessage('Modal.ModelsPortfoliosList.EnterpriseTitle', { name: enterprise.name, count: enterprise.name.endsWith('s') ? 1 : 0 });

    return (
      <div className="models-portfolios-list-v2">
        {loading ? (
          <div className="loading-spinner-container">
            <LoadingSpinner className="initial-loading" message={formatMessage('Modal.ModelsPortfoliosList.LoadingPortfolios')} />
          </div>
        ) : (
          <>
            {selectedOption === MODEL_PORTFOLIO_TYPES.ALL_MODELS &&
              enterprise &&
              this.renderPortfoliosSection(formatMessage('Modal.ModelsPortfoliosList.AllModels'), loadMorePortfolios, portfolios, hasMorePortfolios)}
            {selectedOption === MODEL_PORTFOLIO_TYPES.MY_MODELS &&
              this.renderPortfoliosSection(formatMessage('Modal.ModelsPortfoliosList.MyModels'), loadMoreMyPortfolios, myPortfolios, hasMoreMyPortfolios)}
            {selectedOption === MODEL_PORTFOLIO_TYPES.ENTERPRISE_MODELS &&
              portfolioTitles &&
              enterprise &&
              this.renderPortfoliosSection(portfolioTitles, loadMoreEnterprisePortfolios, enterprisePortfolios, hasMoreEnterprisePortfolios)}
          </>
        )}
      </div>
    );
  }
}

const ModelsPortfoliosList = (props: Props & LegacyProps) => {
  const { isLightVersion } = useThemeVersion();
  if (isLightVersion) {
    return <ModelsPortfoliosListV2 {...props} />;
  }
  return <LegacyModelsPortfoliosList {...props} />;
};

export default ModelsPortfoliosList;
